import { afterNextRender, afterRenderEffect, ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { ChartModule } from 'primeng/chart';
import { ChartData, ChartOptions, ScatterDataPoint } from 'chart.js';
import moment from 'moment';
import 'chartjs-adapter-moment';
import { LayoutService } from '@/layout/service/layout.service';
import { TranslationService } from '@/layout/service/translation.service';
import { ChartPalette, chartColor, readChartPalette } from '../system-monitor-chart/chart-theme';
import { METRIC_MAX_SERIES, metricSeriesColor, withAlpha } from './metric-palette';

export interface MetricPoint {
    /** Epoch milliseconds. */
    t: number;
    v: number;
}

export interface MetricSeries {
    /** Stable identity (e.g. outlet number) - colour follows the key's position in the input. */
    key: string;
    label: string;
    points: MetricPoint[];
}

const TABLE_ROW_LIMIT = 50;

/**
 * Generic time-series chart for metrics (power, and later occupancy / usage).
 * One y-axis only; up to eight categorical series, the rest folded into "Other".
 * Includes a data table view for accessibility and low-contrast series colours.
 */
@Component({
    selector: 'app-metric-chart',
    standalone: true,
    imports: [ChartModule, DecimalPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <span #paletteProbe class="palette-probe" aria-hidden="true"></span>
        <div class="chart-frame" [style.height]="height()">
            @if (palette() && hasData()) {
                <p-chart [type]="type()" [ariaLabel]="ariaLabel()" [data]="chartData()" [options]="chartOptions()"></p-chart>
            } @else if (!hasData()) {
                <div class="chart-empty">{{ emptyText() || t()('metrics.noData') }}</div>
            }
        </div>
        @if (hasData()) {
            <details class="chart-table">
                <summary>{{ t()('metrics.showTable') }}</summary>
                <div class="table-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th scope="col">{{ t()('metrics.time') }}</th>
                                @for (s of displaySeries(); track s.key) {
                                    <th scope="col">{{ s.label }} ({{ unit() }})</th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            @for (row of tableRows(); track row.t) {
                                <tr>
                                    <td>{{ row.label }}</td>
                                    @for (v of row.values; track $index) {
                                        <td>{{ v === null ? '–' : (v | number: '1.0-1') }}</td>
                                    }
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>
            </details>
        }
    `,
    styles: [
        `
            :host {
                display: block;
                min-width: 0;
                width: 100%;
            }
            .palette-probe {
                display: none;
                transition: none !important;
            }
            .chart-frame {
                position: relative;
                width: 100%;
            }
            p-chart {
                display: block;
                position: relative;
                width: 100%;
                height: 100%;
            }
            .chart-empty {
                display: flex;
                align-items: center;
                justify-content: center;
                height: 100%;
                color: var(--text-color-secondary);
                font-size: 0.875rem;
            }
            .chart-table {
                margin-top: 0.5rem;
                font-size: 0.8125rem;
                color: var(--text-color-secondary);
            }
            .chart-table summary {
                cursor: pointer;
                width: fit-content;
            }
            .table-scroll {
                max-height: 16rem;
                overflow: auto;
                margin-top: 0.5rem;
            }
            table {
                border-collapse: collapse;
                width: 100%;
                color: var(--text-color);
            }
            th,
            td {
                padding: 0.25rem 0.5rem;
                text-align: right;
                white-space: nowrap;
                border-bottom: 1px solid var(--surface-border);
            }
            th:first-child,
            td:first-child {
                text-align: left;
            }
        `
    ]
})
export class MetricChartComponent {
    private layoutService = inject(LayoutService);
    private destroyRef = inject(DestroyRef);
    readonly t = inject(TranslationService).t;
    private paletteProbe = viewChild.required<ElementRef<HTMLElement>>('paletteProbe');

    readonly series = input<MetricSeries[]>([]);
    /** Unit shown on the axis, tooltip and table (e.g. "W"). */
    readonly unit = input<string>('');
    /** Window length for axis granularity. */
    readonly windowHours = input<number>(24);
    /** Stack series (use when they sum to a meaningful total, e.g. outlet watts). */
    readonly stacked = input<boolean>(false);
    /** Lines for continuous readings (watts); columns for per-interval totals and shares (occupancy). */
    readonly type = input<'line' | 'bar'>('line');
    /** Bucket length; columns are centred on their interval instead of its start. */
    readonly bucketMinutes = input<number | null>(null);
    /** Fixed y-axis maximum (e.g. 100 for shares); auto when null. */
    readonly yMax = input<number | null>(null);
    readonly ariaLabel = input<string>('');
    readonly height = input<string>('clamp(14rem, 28vw, 20rem)');
    readonly emptyText = input<string>('');

    readonly palette = signal<ChartPalette | null>(null, { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) });
    private readonly dark = signal(false);

    constructor() {
        afterRenderEffect(() => {
            this.layoutService.layoutConfig();
            this.layoutService.transitionComplete();
            this.refreshPalette();
        });
        afterNextRender(() => {
            const observer = new MutationObserver(() => this.refreshPalette());
            observer.observe(this.paletteProbe().nativeElement.ownerDocument.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });
            this.destroyRef.onDestroy(() => observer.disconnect());
        });
    }

    /** Series capped at eight; any beyond are summed into "Other" so no hue is generated. */
    readonly displaySeries = computed<MetricSeries[]>(() => {
        const all = this.series().filter((s) => s.points.length > 0);
        if (all.length <= METRIC_MAX_SERIES) return all;
        const kept = all.slice(0, METRIC_MAX_SERIES - 1);
        const rest = all.slice(METRIC_MAX_SERIES - 1);
        const sums = new Map<number, number>();
        for (const s of rest) for (const p of s.points) sums.set(p.t, (sums.get(p.t) ?? 0) + p.v);
        const other: MetricSeries = {
            key: '__other',
            label: this.t()('metrics.other'),
            points: [...sums.entries()].sort((a, b) => a[0] - b[0]).map(([t, v]) => ({ t, v }))
        };
        return [...kept, other];
    });

    readonly hasData = computed(() => this.displaySeries().length > 0);

    readonly chartData = computed<ChartData<'line' | 'bar', ScatterDataPoint[]>>(() => {
        const dark = this.dark();
        const stacked = this.stacked();
        const series = this.displaySeries();
        const single = series.length === 1;
        if (this.type() === 'bar') {
            const shift = ((this.bucketMinutes() ?? 0) * 60_000) / 2;
            return {
                datasets: series.map((s, i) => ({
                    type: 'bar' as const,
                    label: s.label,
                    data: [...s.points].sort((a, b) => a.t - b.t).map((p) => ({ x: p.t + shift, y: p.v })),
                    backgroundColor: metricSeriesColor(i, dark),
                    borderWidth: 0,
                    borderRadius: 2,
                    borderSkipped: 'start' as const,
                    barPercentage: 0.8,
                    categoryPercentage: stacked ? 1 : 0.9,
                    stack: stacked ? 'metric' : undefined
                }))
            };
        }
        return {
            datasets: series.map((s, i) => {
                const color = metricSeriesColor(i, dark);
                return {
                    type: 'line' as const,
                    label: s.label,
                    data: [...s.points].sort((a, b) => a.t - b.t).map((p) => ({ x: p.t, y: p.v })),
                    borderColor: color,
                    backgroundColor: withAlpha(color, stacked ? 0.35 : 0.1),
                    fill: stacked ? (i === 0 ? 'origin' : '-1') : single,
                    borderWidth: 2,
                    tension: 0.25,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    hitRadius: 8,
                    stack: stacked ? 'metric' : undefined
                };
            })
        };
    });

    readonly chartOptions = computed<ChartOptions<'line' | 'bar'>>(() => {
        const palette = this.palette();
        if (!palette) return {};
        const hrs = this.windowHours();
        const unit = this.unit();
        const stacked = this.stacked();
        const multi = this.displaySeries().length > 1;
        const bar = this.type() === 'bar';
        const bucketMs = (this.bucketMinutes() ?? 0) * 60_000;
        const yMax = this.yMax();
        const text = chartColor(palette.text);
        const muted = chartColor(palette.muted);
        const border = chartColor(palette.border);
        const timeUnit = hrs <= 1 ? 'minute' : hrs <= 48 ? 'hour' : 'day';
        const displayFormats = hrs <= 1 ? { minute: 'HH:mm' } : hrs <= 48 ? { hour: 'HH:mm' } : { day: 'MMM D' };
        const now = Date.now();

        return {
            maintainAspectRatio: false,
            responsive: true,
            animation: false,
            plugins: {
                legend: { display: multi, labels: { color: text, usePointStyle: true, pointStyle: bar ? 'rectRounded' : 'line', padding: 16 } },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    backgroundColor: chartColor(palette.surface),
                    titleColor: text,
                    bodyColor: text,
                    borderColor: border,
                    borderWidth: 1,
                    callbacks: {
                        title: (items) => (bar && bucketMs && items.length ? intervalLabel((items[0].parsed.x ?? 0) - bucketMs / 2, bucketMs) : undefined),
                        label: (ctx) => `${ctx.dataset.label}: ${Number(ctx.parsed.y).toLocaleString(undefined, { maximumFractionDigits: 1 })} ${unit}`
                    }
                }
            },
            interaction: { mode: 'index', intersect: false },
            scales: {
                x: {
                    type: 'time',
                    min: now - hrs * 3600_000,
                    max: now,
                    time: { unit: timeUnit, displayFormats, tooltipFormat: 'MMM D HH:mm' },
                    ticks: { color: muted, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
                    grid: { display: false },
                    border: { color: border }
                },
                y: {
                    type: 'linear',
                    stacked,
                    beginAtZero: true,
                    max: yMax ?? undefined,
                    ticks: { color: muted, maxTicksLimit: 6, callback: (v) => `${v} ${unit}` },
                    grid: { color: chartColor(palette.border, 0.35) },
                    border: { display: false }
                }
            }
        };
    });

    /** Latest rows first, for the table view. */
    readonly tableRows = computed(() => {
        const series = this.displaySeries();
        const times = new Set<number>();
        for (const s of series) for (const p of s.points) times.add(p.t);
        const lookups = series.map((s) => new Map(s.points.map((p) => [p.t, p.v])));
        return [...times]
            .sort((a, b) => b - a)
            .slice(0, TABLE_ROW_LIMIT)
            .map((t) => ({ t, label: moment(t).format('MMM D HH:mm'), values: lookups.map((m) => m.get(t) ?? null) }));
    });

    private refreshPalette(): void {
        const probe = this.paletteProbe().nativeElement;
        this.dark.set(probe.ownerDocument.documentElement.classList.contains('app-dark'));
        this.palette.set(readChartPalette(probe));
    }
}

/** "Sep 29 09:00 – 10:00" for an interval that starts at `start` (epoch ms); days as "Sep 29". */
export function intervalLabel(start: number, lengthMs: number): string {
    if (lengthMs >= 86_400_000) return moment(start).format('MMM D');
    return `${moment(start).format('MMM D HH:mm')} – ${moment(start + lengthMs).format('HH:mm')}`;
}
